import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import axios from 'axios'
import { useEffect, useRef } from 'react'

import { CacheKeys } from '../lib/cache'
import { toCamelCase, toSnakeCase } from '../lib/typeHelper'
import {
  buildSalesLeadParams,
  type ClosedLostReason,
  type SalesLeadFilters,
  type SalesLeadStatus,
} from './salesLead'
import type { SalesLeadTag } from './salesLeadTag'

const BASE = '/api/v1/insure/internal/lead-action-automations'
const KEY = ['lead-action-automation']

// Poll a running run's counts so progress moves while the dialog is open.
const RUNNING_POLL_MS = 5000

export enum LeadAction {
  GEICO_ELIGIBILITY = 'GEICO_ELIGIBILITY',
  COVERWHALE_ELIGIBILITY = 'COVERWHALE_ELIGIBILITY',
  FOLLOW = 'FOLLOW',
  ADD_TAG = 'ADD_TAG',
  ASSIGN = 'ASSIGN',
  CHANGE_STATUS = 'CHANGE_STATUS',
}

export const LEAD_ACTION_LABELS: Record<LeadAction, string> = {
  [LeadAction.GEICO_ELIGIBILITY]: 'GEICO eligibility',
  [LeadAction.COVERWHALE_ELIGIBILITY]: 'CoverWhale eligibility',
  [LeadAction.FOLLOW]: 'Follow',
  [LeadAction.ADD_TAG]: 'Add tag',
  [LeadAction.ASSIGN]: 'Assign',
  [LeadAction.CHANGE_STATUS]: 'Change status',
}

export enum LeadActionRunStatus {
  RUNNING = 'RUNNING',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

export const LEAD_ACTION_RUN_STATUS_LABELS: Record<LeadActionRunStatus, string> = {
  [LeadActionRunStatus.RUNNING]: 'Running',
  [LeadActionRunStatus.COMPLETED]: 'Completed',
  [LeadActionRunStatus.CANCELLED]: 'Stopped',
}

export interface LeadActionCounts {
  total: number
  /** Not reached yet, including the lead being checked right now. */
  pending: number
  eligible: number
  notEligible: number
  /** Leads a Follow, Add tag, Assign or Change status run finished. */
  done: number
  /** Leads the action skipped, e.g. no DOT number or already in that status. */
  skipped: number
  /** Checks that came back without a verdict. */
  failed: number
}

/** The person an Assign run gives each lead to. */
export interface LeadActionAssignee {
  id: string
  name: string
  avatar: string
}

/** Verdict tag ids an eligibility run writes, so its counts can filter the leads list. */
export interface LeadActionVerdictTags {
  eligible: string | null
  notEligible: string | null
}

export interface LeadActionAutomation {
  id: string
  action: LeadAction
  status: LeadActionRunStatus
  leadFilters: Record<string, string>
  leadCounts: LeadActionCounts
  filterSummary: string[]
  verdictTags: LeadActionVerdictTags | null
  /** What an Add tag run puts on each lead; empty for other actions. */
  tags: SalesLeadTag[]
  /** Who an Assign run gives each lead to; null for other actions. */
  assignee: LeadActionAssignee | null
  /** The status a Change status run moves each lead to; null for other actions. */
  targetStatus: SalesLeadStatus | null
  createdAt: string
  updatedAt: string
  completedAt: string | null
}

export interface LeadActionPreview {
  count: number
  summary: string[]
  requiresConfirmation: boolean
  confirmationThreshold: number
}

export interface CreateLeadActionInput {
  /** One run per action, each over the same leads. */
  actions: LeadAction[]
  leadFilters: Record<string, string>
  confirmed: boolean
  /** Tags for the Add tag run; ignored when Add tag isn't picked. */
  tagIds: string[]
  /** The person for the Assign run; null when Assign isn't picked. */
  assigneeId: string | null
  /** The status for the Change status run; null when Change status isn't picked. */
  targetStatus: SalesLeadStatus | null
  /** Why, when that status is Closed/Lost, and the note each lead's stage change carries. */
  closedLostReason: ClosedLostReason | null
  transitionNotes: string
}

/** How many leads a run over these filters would process. */
export function useLeadActionPreview(filters: SalesLeadFilters) {
  const params = buildSalesLeadParams(filters)
  return useQuery({
    queryKey: [...KEY, 'preview', params],
    queryFn: () =>
      axios.get(`${BASE}/preview/`, { params }).then((r) => toCamelCase(r.data) as LeadActionPreview),
  })
}

/** The rep's recent runs, newest first. Polls while one is still running. */
export function useLeadActionAutomations() {
  return useQuery({
    queryKey: [...KEY, 'list'],
    queryFn: () => axios.get(`${BASE}/`).then((r) => toCamelCase(r.data) as LeadActionAutomation[]),
    refetchInterval: (query) =>
      query.state.data?.some((run) => run.status === LeadActionRunStatus.RUNNING) ? RUNNING_POLL_MS : false,
  })
}

/** Refetches the leads list when one of the rep's runs finishes, so its changes show without a reload. */
export function useRefetchLeadsOnRunFinish(runs: LeadActionAutomation[] | undefined) {
  const qc = useQueryClient()
  // Newest finish time seen; a small run can be over before the list first shows it running.
  const lastFinishedAt = useRef<number | null>(null)
  useEffect(() => {
    if (!runs) return
    const finishedAt = Math.max(0, ...runs.map((run) => (run.completedAt ? Date.parse(run.completedAt) : 0)))
    if (lastFinishedAt.current !== null && finishedAt > lastFinishedAt.current) {
      qc.invalidateQueries({ queryKey: [CacheKeys.SALES_LEADS] })
    }
    lastFinishedAt.current = finishedAt
  }, [runs, qc])
}

export function useCreateLeadActionAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateLeadActionInput) =>
      axios.post(`${BASE}/`, toSnakeCase(input)).then((r) => toCamelCase(r.data) as LeadActionAutomation[]),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      // The run creates its verdict tags up front; the tag filter should offer them.
      qc.invalidateQueries({ queryKey: [CacheKeys.SALES_LEAD_TAGS] })
    },
  })
}

export function useCancelLeadActionAutomation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${BASE}/${id}/cancel/`).then((r) => toCamelCase(r.data) as LeadActionAutomation),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}
